// components/DegreeAudit/Progress/GenEdDualProgressBar.tsx

import { memo, useMemo } from 'react';
import { Check, AlertCircle } from 'lucide-react';

interface ConstraintResult {
  satisfied?: boolean;
  area?: string;
  type?: string;
  message?: string;
}

interface RequirementShape {
  credits_required?: number;
  credits_completed?: number;
  credits_in_progress?: number;
  credits_planned?: number;
  credits_suggested?: number;
  is_satisfied?: boolean;
  progress_percentage?: number;
  constraint_results?: ConstraintResult[];
}

interface GenEdNode {
  name?: string;
  children?: GenEdNode[];
  requirement?: RequirementShape;
}

export interface GenEdProgram {
  credits_required?: number;
  requirements?: GenEdNode[];
  [key: string]: unknown;
}

interface StatusCredits {
  completed: number;
  inProgress: number;
  planned: number;
  suggested: number;
}

/**
 * Cap one requirement's credits at `credits_required`, filling in priority
 * order (completed → in-progress → planned → suggested). The four returned
 * values sum to the same capped "engaged" total the bar shows, but broken
 * out per status so each renders in its own status color instead of a single
 * lumped segment. Matches the status contract used by the top-level progress
 * bar and RequirementFulfillmentBar (planned = yellow, not purple).
 */
const capByStatus = (req: RequirementShape): StatusCredits => {
  let remaining = req.credits_required || 0;
  const take = (v: number | undefined): number => {
    const taken = Math.min(Math.max(v || 0, 0), Math.max(remaining, 0));
    remaining -= taken;
    return taken;
  };
  return {
    completed: take(req.credits_completed),
    inProgress: take(req.credits_in_progress),
    planned: take(req.credits_planned),
    suggested: take(req.credits_suggested),
  };
};

interface GroupSummary {
  name: string;
  creditsRequired: number;
  creditsCompleted: number;
  creditsInProgress: number;
  creditsPlanned: number;
  creditsSuggested: number;
  creditsEngaged: number;
  childrenTotal: number;
  childrenSatisfied: number;
  isSatisfied: boolean;
  constraintWarnings: string[];
}

export interface GenEdDualProgressBarProps {
  program: GenEdProgram;
}

/**
 * Category-based progress summary for gen-ed programs.
 *
 * Renders one bar per top-level requirement in `program.requirements` —
 * works for any number of GE groups. UMD has 4 (Fundamental Studies,
 * Distributive Studies, I-Series, Diversity); CSU's Cal-GETC has 6
 * (Areas 1-6). The component is data-driven; nothing about it is
 * hardcoded to a particular institution.
 *
 * If `program.credits_required` isn't set, the total denominator falls
 * back to the sum of group credit requirements derived from the data.
 * This is more honest than a UMD-specific magic number.
 */
const GenEdDualProgressBar = memo(({ program }: GenEdDualProgressBarProps) => {

  const groups: GroupSummary[] = useMemo(() => {
    const requirements = program.requirements || [];
    return requirements.map((node) => {
      const name = node.name || 'Unknown';
      const children = node.children || [];
      const req = node.requirement || {};

      if (children.length > 0) {
        let groupCreditsRequired = 0;
        let creditsCompleted = 0;
        let creditsInProgress = 0;
        let creditsPlanned = 0;
        let creditsSuggested = 0;
        let childrenSatisfied = 0;
        const constraintWarnings: string[] = [];

        for (const child of children) {
          const cr = child.requirement || {};
          const sc = capByStatus(cr);

          groupCreditsRequired += cr.credits_required || 0;
          creditsCompleted += sc.completed;
          creditsInProgress += sc.inProgress;
          creditsPlanned += sc.planned;
          creditsSuggested += sc.suggested;

          if (cr.is_satisfied || (cr.progress_percentage ?? 0) >= 100) {
            childrenSatisfied++;
          }

          const constraints = cr.constraint_results || [];
          for (const c of constraints) {
            if (c.satisfied === false) {
              if (c.area) {
                constraintWarnings.push(`Needs ${c.area} course`);
              } else if (c.type === 'at_least_outside_major') {
                constraintWarnings.push(c.message || 'Needs course outside major');
              }
            }
          }
        }

        return {
          name,
          creditsRequired: groupCreditsRequired,
          creditsCompleted,
          creditsInProgress,
          creditsPlanned,
          creditsSuggested,
          creditsEngaged:
            creditsCompleted + creditsInProgress + creditsPlanned + creditsSuggested,
          childrenTotal: children.length,
          childrenSatisfied,
          isSatisfied: childrenSatisfied === children.length,
          constraintWarnings,
        };
      }

      const sc = capByStatus(req);
      const isSatisfied = !!req.is_satisfied || (req.progress_percentage || 0) >= 100;

      const constraintWarnings: string[] = [];
      for (const c of req.constraint_results || []) {
        if (c.satisfied === false) {
          if (c.area) {
            constraintWarnings.push(`Needs ${c.area} course`);
          } else if (c.type === 'at_least_outside_major') {
            constraintWarnings.push(c.message || 'Needs course outside major');
          }
        }
      }

      return {
        name,
        creditsRequired: req.credits_required || 0,
        creditsCompleted: sc.completed,
        creditsInProgress: sc.inProgress,
        creditsPlanned: sc.planned,
        creditsSuggested: sc.suggested,
        creditsEngaged: sc.completed + sc.inProgress + sc.planned + sc.suggested,
        childrenTotal: 0,
        childrenSatisfied: 0,
        isSatisfied,
        constraintWarnings,
      };
    });
  }, [program.requirements]);

  return (
    <div className="w-full space-y-1.5">
      {groups.map((group, idx) => {
        // Per-status widths against the group's required credits. Segments
        // stack in the same priority order they were capped in, so they read
        // completed → in-progress → planned → suggested left to right and
        // never exceed 100% combined.
        const toPct = (credits: number): number =>
          group.creditsRequired > 0
            ? Math.min((credits / group.creditsRequired) * 100, 100)
            : 0;
        const completedPct = toPct(group.creditsCompleted);
        const inProgressPct = toPct(group.creditsInProgress);
        const plannedPct = toPct(group.creditsPlanned);
        const suggestedPct = toPct(group.creditsSuggested);

        return (
          <div key={idx} className="flex items-center gap-2 text-xs">
            <div className="w-4 flex-shrink-0">
              {group.isSatisfied ? (
                <Check className="w-3.5 h-3.5 text-green-500" />
              ) : group.constraintWarnings.length > 0 ? (
                <AlertCircle className="w-3.5 h-3.5 text-status-warning" />
              ) : (
                <div className="w-3.5 h-3.5" />
              )}
            </div>

            <span
              className={`w-36 truncate font-medium ${group.isSatisfied ? 'text-ink-3' : 'text-ink'}`}
            >
              {group.name}
            </span>

            {/* Per-status segments matching the app's status contract
                (completed=green, in-progress=blue, planned=yellow,
                suggested=purple) — same as RequirementFulfillmentBar and
                the top-level progress bar. Planned credits render yellow,
                not purple. */}
            <div className="flex-1 h-2.5 bg-surface-sunken rounded-full overflow-hidden">
              {completedPct > 0 && (
                <div
                  className="h-full bg-green-500 float-left transition-all duration-300"
                  title={`Completed (${group.creditsCompleted} cr)`}
                  style={{ width: `${completedPct}%` }}
                />
              )}
              {inProgressPct > 0 && (
                <div
                  className="h-full bg-blue-500 float-left transition-all duration-300"
                  title={`In progress (${group.creditsInProgress} cr)`}
                  style={{ width: `${inProgressPct}%` }}
                />
              )}
              {plannedPct > 0 && (
                <div
                  className="h-full bg-yellow-500 float-left transition-all duration-300"
                  title={`Planned (${group.creditsPlanned} cr)`}
                  style={{ width: `${plannedPct}%` }}
                />
              )}
              {suggestedPct > 0 && (
                <div
                  className="h-full bg-status-suggested/50 float-left transition-all duration-300"
                  title={`AI-suggested (${group.creditsSuggested} cr)`}
                  style={{ width: `${suggestedPct}%` }}
                />
              )}
            </div>

            <span
              className={`w-20 text-right tabular-nums ${
                group.isSatisfied ? 'text-ink-3' : 'text-ink-2'
              }`}
            >
              {group.creditsEngaged}/{group.creditsRequired} cr
            </span>
          </div>
        );
      })}

      <div className="border-t border-line-soft pt-1 mt-1">
        <div className="flex items-center justify-between text-xs text-ink-3">
          <span className="font-medium">Total Gen Ed</span>
          {/* Compact legend — shows only the statuses actually present so it
              stays honest (planned = yellow, suggested = purple), matching
              the segment colors above and the app-wide status contract. */}
          <span className="flex items-center gap-3">
            {groups.some((g) => g.creditsCompleted > 0) && (
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-green-500" aria-hidden />
                Completed
              </span>
            )}
            {groups.some((g) => g.creditsInProgress > 0) && (
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-blue-500" aria-hidden />
                In progress
              </span>
            )}
            {groups.some((g) => g.creditsPlanned > 0) && (
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-yellow-500" aria-hidden />
                Planned
              </span>
            )}
            {groups.some((g) => g.creditsSuggested > 0) && (
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-status-suggested/50" aria-hidden />
                Suggested
              </span>
            )}
            <span className="tabular-nums">
              {groups.reduce((sum, g) => sum + g.creditsEngaged, 0)} of{' '}
              {program.credits_required ||
                groups.reduce((sum, g) => sum + g.creditsRequired, 0)}{' '}
              credits
            </span>
          </span>
        </div>
      </div>
    </div>
  );
});

GenEdDualProgressBar.displayName = 'GenEdDualProgressBar';

export default GenEdDualProgressBar;
